import { record, type Row } from '../ability/context.js';
import { stairGeometry, type RestShape } from '../inventory/floor-move.js';
import type { WalkPoint } from './plain-walk.js';
import { floorBandRoomForPosition } from '../../encounter-creation-engine/floor-band.js';

/** Already projected inventory: the scene's selected look and effective scales. */
interface StairInventory {
  id: string;
  positionFt: WalkPoint;
  runtimeMeshes?: { id: string }[];
  meshScaleXYZ?: number[];
  scale?: number;
  rotationDegrees?: number;
}

/** Project measured inventory stairs into the existing walking contract, without recompiling the scene. */
export function withInventoryStairs(shape: RestShape, inventory: readonly StairInventory[], assets: readonly unknown[]): RestShape {
  const connections: Row[] = [];
  const profiles = new Map(assets.map(record).filter((a): a is Row => a !== null).map(a => [a.meshId, a]));
  for (const row of inventory) {
    if (row.runtimeMeshes?.length !== 1) continue;
    const asset = profiles.get(row.runtimeMeshes[0]!.id);
    const geometry = record(asset?.geometry), route = record(Array.isArray(geometry?.routes) ? geometry.routes[0] : null);
    if (route?.kind !== 'stair' || route.walkable !== true || !Array.isArray(route.centerlineM) || route.centerlineM.length !== 2) continue;
    const rotation = (row.rotationDegrees ?? 0) / 90;
    if (!Number.isInteger(rotation)) continue;
    const q = ((rotation % 4) + 4) % 4;
    const axes = (row.meshScaleXYZ ?? [1, 1, 1]).map(n => n * (row.scale ?? 1));
    const points = route.centerlineM.map(p => {
      if (!Array.isArray(p) || p.length !== 3) return [NaN, NaN, NaN];
      const [x, z, y] = p.map((n, i) => Number(n) * axes[i]! / .3048) as [number, number, number];
      const [rx, ry] = [[x, y], [y, -x], [-x, -y], [-y, x]][q]!;
      return [row.positionFt.x + rx!, row.positionFt.y + ry!, row.positionFt.z + z];
    });
    if (points.some(p => !p.every(Number.isFinite))) continue;
    const dx = points[1]![0]! - points[0]![0]!, dy = points[1]![1]! - points[0]![1]!, run = Math.hypot(dx, dy);
    if (run < 1) continue;
    // Floors support the terminal aprons. A rounded tread endpoint can be just short of the deck edge.
    const source = floorBandRoomForPosition(shape.boxes, points[0]![0]! - dx / run * 2.65,
      points[0]![1]! - dy / run * 2.65, points[0]![2]!);
    const target = floorBandRoomForPosition(shape.boxes, points[1]![0]! + dx / run * 2.65,
      points[1]![1]! + dy / run * 2.65, points[1]![2]!);
    if (!source || !target || target.floor <= source.floor
      || Math.abs(source.floor - points[0]![2]!) > .01 || Math.abs(target.floor - points[1]![2]!) > .01) continue;
    // Stored positions/scales are rounded. Use their contacting physical floors, as native stairs do.
    const connection = { id: row.id, kind: 'stair', sourceRoomId: source.id, targetRoomId: target.id,
      anchorFt: { ...row.positionFt, z: source.floor }, rotationQuarterTurns: q,
      scaleXYZ: [axes[0], (target.floor - source.floor) * .3048
        / (Number(route.centerlineM[1][1]) - Number(route.centerlineM[0][1])), axes[2]],
      traversalEnabled: true, asset };
    try { stairGeometry(connection, shape.boxes); } catch { continue; }
    connections.push(connection);
  }
  if (!connections.length) return shape;
  const existing = shape.authoring?.verticalConnections;
  return { ...shape, authoring: { ...shape.authoring,
    verticalConnections: [...(Array.isArray(existing) ? existing : []), ...connections] } };
}

/** A floor click at the far landing follows the same route as the stair's direction button. */
export function stairWalkForClick(shape: RestShape | null, origin: WalkPoint, destination: WalkPoint,
  pointAtElevation?: (z: number) => { x: number; y: number } | null):
  { stair: { id: string; direction: 'up' | 'down' }; destination: WalkPoint; route: number[][] } | null {
  const connections = shape?.authoring?.verticalConnections;
  if (!shape || !Array.isArray(connections)) return null;
  for (const raw of connections) {
    const connection = record(raw);
    if (!connection) continue;
    let geometry: ReturnType<typeof stairGeometry>;
    try { geometry = stairGeometry(connection, shape.boxes); } catch { continue; }
    const direction = Math.abs(origin.z - geometry.points[3]![2]!) <= .01 ? 'down' : 'up';
    const points = direction === 'down' ? geometry.points.slice().reverse() : geometry.points;
    const first = points[0]!, last = points[points.length - 1]!;
    const picked = pointAtElevation ? pointAtElevation(last[2]!) : destination;
    if (!picked || Math.abs(origin.z - first[2]!) > .01
      || Math.hypot(origin.x - first[0]!, origin.y - first[1]!) > 3
      || Math.hypot(picked.x - last[0]!, picked.y - last[1]!) > 3) continue;
    return { stair: { id: String(connection.id), direction },
      destination: { x: last[0]!, y: last[1]!, z: last[2]! }, route: [[origin.x, origin.y, origin.z], ...points]
        .filter((p, i, all) => i === 0 || Math.hypot(...p.map((n, j) => n - all[i - 1]![j]!)) > 1e-9) };
  }
  return null;
}

/** Only the inventory actually supporting this foot is exempt from its enclosing mesh volume. */
export function stairSupportingInventory(shape: RestShape, point: WalkPoint): string | undefined {
  const connections = shape.authoring?.verticalConnections;
  if (!Array.isArray(connections)) return undefined;
  for (const raw of connections) {
    const connection = record(raw);
    if (!connection) continue;
    let geometry: ReturnType<typeof stairGeometry>;
    try { geometry = stairGeometry(connection, shape.boxes); } catch { continue; }
    for (let i = 1; i < geometry.points.length; i++) {
      const a = geometry.points[i - 1]!, b = geometry.points[i]!;
      const dx = b[0]! - a[0]!, dy = b[1]! - a[1]!, dz = b[2]! - a[2]!;
      const t = ((point.x - a[0]!) * dx + (point.y - a[1]!) * dy) / (dx * dx + dy * dy);
      if (t >= -.0001 && t <= 1.0001 && Math.abs(point.z - a[2]! - t * dz) <= .002
        && Math.hypot(point.x - a[0]! - t * dx, point.y - a[1]! - t * dy) <= geometry.width / 2 + .002)
        return String(connection.id);
    }
  }
  return undefined;
}
